import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PilotModeBanner from './PilotModeBanner';
import {
  House,
  ClipboardText,
  Storefront,
  FileText,
  Truck,
  Gear,
  SignOut,
  List,
  X,
  Package,
  Anchor,
  SealCheck,
  ChartBar,
  ShoppingCart,
  Users,
  Archive,
  Cube,
  UserGear
} from '@phosphor-icons/react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getNavItems = () => {
    const baseItems = [
      { to: '/settings', icon: Gear, label: 'Settings' }
    ];

    switch (user?.role) {
      case 'purchasing_manager':
      case 'system_admin':
        // PM: Full access to Dashboard, Requisitions, Vendors, POs, Closed POs, Item Master, Reports, Shipments, Freight Forwarders, User Management
        return [
          { to: '/dashboard', icon: House, label: 'Dashboard' },
          { to: '/requisitions', icon: ClipboardText, label: 'Purchase Requisitions' },
          { to: '/vendors', icon: Storefront, label: 'Vendors' },
          { to: '/purchase-orders', icon: FileText, label: 'Purchase Orders' },
          { to: '/closed-pos', icon: SealCheck, label: 'Closed POs' },
          { to: '/storekeeper-portal', icon: Cube, label: 'Item Master' },
          { to: '/digital-count', icon: ClipboardText, label: 'Digital Count' },
          { to: '/receiving-records', icon: Archive, label: 'Receiving Records' },
          { to: '/reports', icon: ChartBar, label: 'Reports' },
          { to: '/shipments', icon: Truck, label: 'Shipments' },
          { to: '/freight-forwarders', icon: Anchor, label: 'Freight Forwarders' },
          { to: '/user-management', icon: UserGear, label: 'User Management' },
          { to: '/admin/sandboxes', icon: Storefront, label: 'Sandbox Management' },
          ...baseItems
        ];
      case 'buyer':
        // Buyer: Only their requisitions portal - NO Reports
        return [
          { to: '/buyer-portal', icon: ShoppingCart, label: 'My Requisitions' }
        ];
      case 'department_head':
        // DH: Approval Queue + Reports (Spend by Dept only)
        return [
          { to: '/dh-portal', icon: Users, label: 'Approval Queue' },
          { to: '/reports', icon: ChartBar, label: 'Reports' },
          ...baseItems
        ];
      case 'receiver':
        // Receiver: Only pending receiving - NO Reports
        return [
          { to: '/receiver-portal', icon: Archive, label: 'Pending Receiving' }
        ];
      case 'accounts_payable':
        // Accounts Payable: Read-only view of closed POs in the Receiver Portal screen.
        return [
          { to: '/receiver-portal', icon: Archive, label: 'Accounts Payable' }
        ];
      case 'storekeeper':
        // Storekeeper: Inventory + Digital Count + Reports (Inventory reports only)
        return [
          { to: '/storekeeper-portal', icon: Cube, label: 'Inventory' },
          { to: '/digital-count', icon: ClipboardText, label: 'Digital Count' },
          { to: '/reports', icon: ChartBar, label: 'Reports' }
        ];
      case 'internal_requester':
        // Internal Requester: Only their requisitions portal - NO Reports
        return [
          { to: '/internal-requester-portal', icon: ClipboardText, label: 'My Requisitions' }
        ];
      case 'financial_controller':
        // FC: Approval Queue (dashboard) + Digital Count Reports + Reports
        return [
          { to: '/fc-approval', icon: ClipboardText, label: 'Approvals & Payments' },
          { to: '/digital-count', icon: ClipboardText, label: 'Digital Count' },
          { to: '/reports', icon: ChartBar, label: 'Reports' },
          ...baseItems
        ];
      case 'vendor':
        // Vendor: Only their PO portal - NO Reports
        return [
          { to: '/vendor-portal', icon: Package, label: 'My Purchase Orders' }
        ];
      case 'freight_forwarder':
        // Freight Forwarder: Only their shipments portal - NO Reports
        return [
          { to: '/freight-portal', icon: Anchor, label: 'My Shipments' }
        ];
      default:
        return baseItems;
    }
  };

  const navItems = getNavItems();

  return (
    <div className="min-h-screen bg-[#F8FAFC] app-shell" data-app-shell>
      {/* Mobile header */}
      <div 
        className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white/80 border-b border-slate-100 z-50 flex items-center justify-between px-4"
        style={{
          WebkitBackdropFilter: 'blur(24px)',
          backdropFilter: 'blur(24px)',
          WebkitTransform: 'translateZ(0)',
          transform: 'translateZ(0)'
        }}
      >
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          data-testid="mobile-menu-btn"
        >
          <List size={24} className="text-slate-600" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg gradient-ocean flex items-center justify-center">
            <Truck size={18} weight="fill" className="text-white" />
          </div>
          <span className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            HERChain
          </span>
        </div>
        <div className="w-10" />
      </div>

      {/* Sidebar overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-100 z-50
          flex flex-col
          ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed lg:sidebar-open'}
        `}
        style={{
          transform: sidebarOpen ? 'translate3d(0, 0, 0)' : undefined,
          WebkitTransform: sidebarOpen ? 'translate3d(0, 0, 0)' : undefined,
          willChange: 'transform',
          WebkitBackfaceVisibility: 'hidden',
          backfaceVisibility: 'hidden'
        }}
      >
        {/* Logo */}
        <div className="p-6 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl gradient-ocean flex items-center justify-center shadow-lg shadow-[#0F52BA]/20">
                <Truck size={22} weight="fill" className="text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  HERChain
                </h1>
                <p className="text-xs text-slate-500">Solutions</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100"
              data-testid="close-sidebar-btn"
            >
              <X size={20} className="text-slate-500" />
            </button>
          </div>
        </div>

        {/* User info */}
        <div className="p-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#0F52BA]/10 flex items-center justify-center">
              <span className="text-sm font-semibold text-[#0F52BA]">
                {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-500 capitalize">
                {user?.role?.replace('_', ' ')}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation - scrollable area */}
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto" data-testid="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'text-[#0F52BA] bg-[#F0F7FF] font-medium'
                    : 'text-slate-600 hover:text-[#0F52BA] hover:bg-[#F0F7FF]'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon size={20} weight="duotone" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Logout - fixed at bottom */}
        <div className="p-4 border-t border-slate-100 flex-shrink-0 bg-white">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-slate-600 hover:text-[#E63946] hover:bg-red-50 transition-all duration-200"
            data-testid="logout-btn"
          >
            <SignOut size={20} weight="duotone" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <PilotModeBanner />
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
